import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  RECEIVED_WINDOWS,
  REVIEW_FILTERS,
  EMPTY_LEAD_FILTERS,
  hasActiveLeadFilters,
  type DealLeadFilterState,
} from '@/features/deal-review/deal-lead-views'
import {
  quietToneClasses,
  triggerToneClasses,
} from '@/features/deal-review/review-tones'
import { getLeadDispositionOption } from '@/features/deal-review/review-options'
import type { DealLeadListItem } from '@/types/api'

export function DecisionFilter({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div
      role="group"
      aria-label="Filter reviewed deals by decision"
      className="flex flex-wrap items-center gap-1.5"
    >
      {REVIEW_FILTERS.map((filter) => {
        const isSelected = value === filter.value
        const tone =
          filter.value === 'all'
            ? 'neutral'
            : getLeadDispositionOption(filter.value).tone
        const quiet = quietToneClasses[tone]

        return (
          <Button
            key={filter.value}
            type="button"
            variant="outline"
            aria-pressed={isSelected}
            onClick={() => onChange(filter.value)}
            className={cn(
              'h-7 gap-1.5 rounded-full px-2.5 text-xs shadow-none',
              isSelected
                ? cn(triggerToneClasses[tone], 'font-medium')
                : quiet.trigger
            )}
          >
            {filter.value !== 'all' && (
              <span
                aria-hidden="true"
                className={cn('size-1.5 shrink-0 rounded-full', quiet.dot)}
              />
            )}
            {filter.label}
          </Button>
        )
      })}
    </div>
  )
}

function distinctValues(
  leads: DealLeadListItem[],
  key: 'brokerCompany' | 'sector'
): string[] {
  const values = new Set<string>()

  for (const lead of leads) {
    const value = lead[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      values.add(value)
    }
  }

  return [...values].sort()
}

// Values accumulate: options come from loaded rows, so filtering would shrink the menu to the pick.
function useSeenValues(values: string[], selected: string[]): string[] {
  const [seen, setSeen] = useState<string[]>([])

  // Set during render: derived state, so an effect would paint the short menu first.
  const missing = [...values, ...selected].filter(
    (value) => value.length > 0 && !seen.includes(value)
  )
  if (missing.length > 0) {
    setSeen(
      [...new Set([...seen, ...missing])].sort((a, b) => a.localeCompare(b))
    )
  }

  return seen
}

export function InboxToolbar({
  state,
  onChange,
  leads,
  leading,
}: {
  state: DealLeadFilterState
  onChange: (next: DealLeadFilterState) => void
  leads: DealLeadListItem[]
  leading?: ReactNode
}) {
  const sources = useSeenValues(
    useMemo(() => distinctValues(leads, 'brokerCompany'), [leads]),
    state.source
  )
  const industries = useSeenValues(
    useMemo(() => distinctValues(leads, 'sector'), [leads]),
    state.industry
  )

  function set<K extends keyof DealLeadFilterState>(
    key: K,
    value: DealLeadFilterState[K]
  ) {
    onChange({ ...state, [key]: value })
  }

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
      {leading}
      {leading && (
        <Separator
          orientation="vertical"
          className="hidden h-7 bg-primary/10 lg:block"
        />
      )}
      <div className="relative w-full lg:w-70">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-2.5 size-4 text-primary/70"
        />
        <Input
          aria-label="Search deals"
          placeholder="Search deals"
          value={state.search}
          onChange={(event) => set('search', event.target.value)}
          className="pl-9"
        />
      </div>

      <FilterMultiSelect
        label="Filter deals by source"
        selected={state.source}
        allLabel="All sources"
        noun="source"
        options={sources}
        onChange={(value) => set('source', value)}
      />

      <FilterSelect
        label="Filter deals by received date"
        value={state.received}
        allLabel="All received dates"
        options={RECEIVED_WINDOWS.map((window) => ({ ...window }))}
        onChange={(value) => set('received', value)}
      />

      <FilterMultiSelect
        label="Filter deals by industry"
        selected={state.industry}
        allLabel="All industries"
        noun="industry"
        plural="industries"
        options={industries}
        onChange={(value) => set('industry', value)}
      />

      {hasActiveLeadFilters(state) && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-primary/70"
          onClick={() => onChange(EMPTY_LEAD_FILTERS)}
        >
          Clear
        </Button>
      )}
    </div>
  )
}

function FilterMultiSelect({
  label,
  selected,
  allLabel,
  noun,
  plural,
  options,
  onChange,
}: {
  label: string
  selected: string[]
  allLabel: string
  noun: string
  plural?: string
  options: string[]
  onChange: (value: string[]) => void
}) {
  const [open, setOpen] = useState(false)

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? selected[0]
        : `${selected.length} ${plural ?? `${noun}s`}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className="w-full justify-between gap-2 bg-white px-3 text-xs font-normal text-primary sm:w-52 lg:w-44"
        >
          <span className="truncate">{summary}</span>
          <ChevronDown aria-hidden="true" className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandList>
            <CommandEmpty>No {plural ?? `${noun}s`} yet.</CommandEmpty>
            <CommandGroup className="p-1">
              {options.map((option) => {
                const checked = selected.includes(option)

                return (
                  <CommandItem
                    key={option}
                    value={option}
                    aria-checked={checked}
                    onSelect={() =>
                      onChange(
                        checked
                          ? selected.filter((value) => value !== option)
                          : [...selected, option]
                      )
                    }
                  >
                    {/* A drawn box: a Checkbox button inside a clickable row is invalid nesting. */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mr-2 flex size-3.75 shrink-0 items-center justify-center rounded-[4px] border',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-primary/55'
                      )}
                    >
                      {checked && <Check className="size-3" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{option}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t border-primary/10 p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs text-primary/70"
              onClick={() => onChange([])}
            >
              {allLabel}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function FilterSelect({
  label,
  value,
  allLabel,
  options,
  onChange,
}: {
  label: string
  value: string
  allLabel: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label={label}
        className="w-full bg-white sm:w-52 lg:w-44"
      >
        <SelectValue placeholder={allLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
